/**
 * Merged tab — shows this conversation's merge relationships:
 *   • If this is a SECONDARY (mergedIntoId set) → the PRIMARY it was merged into.
 *   • If this is a PRIMARY → the SECONDARY conversations merged into it.
 *
 * Primary comes from GET /conversations/:mergedIntoId; secondaries from the
 * list endpoint filtered by ?mergedIntoId= (which lifts the default
 * hide-merged exclusion). Mirrors ParentChildTab.
 */
import { useEffect, useState, type JSX } from 'react'
import { ChevronRight, GitMerge, User, Calendar } from 'lucide-react'
import { api } from '../../../api/client.js'
import { R } from './primitives.js'
import { formatInTimezone } from '../../../utils/timezone.js'
import { formatCustomerName } from '../../../utils/formatCustomerName.js'

interface MergedTabProps {
  conversationId: string
  /** Non-null when THIS conversation is a secondary — the id of its primary. */
  mergedIntoId: string | null
  onNavigateToConversation: (conversationId: string) => void
}

interface RelatedCard {
  id: string
  referenceId: string
  subject: string | null
  status: string
  client: string | null
  agent: string | null
  priority: string | null
  channel: string | null
  openedAt: string | null
}

const STATUS_PILL: Record<string, string> = {
  open: 'bg-emerald-50 text-emerald-700',
  reopen: 'bg-blue-50 text-blue-700',
  on_hold: 'bg-amber-50 text-amber-700',
  closed: 'bg-slate-100 text-slate-600',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return formatInTimezone(d, { day: '2-digit', month: 'short', year: 'numeric' })
}

function RelatedCards({
  cards,
  onNavigate,
}: {
  cards: RelatedCard[]
  onNavigate: (id: string) => void
}): JSX.Element {
  return (
    <div className="space-y-2">
      {cards.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onNavigate(c.id)}
          className={`group flex w-full items-center gap-3 ${R} border border-slate-200 bg-white p-3 text-left transition hover:border-violet-300 hover:shadow-sm`}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-50">
            <GitMerge className="h-4 w-4 text-violet-500" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-800">#{c.referenceId}</span>
              <span className={`rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium ${STATUS_PILL[c.status] ?? 'bg-slate-100 text-slate-600'}`}>
                {c.status}
              </span>
            </div>
            <div className="truncate text-xs text-slate-500">{c.subject || '(No subject)'}</div>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-400">
              {c.client && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{c.client}</span>}
              <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(c.openedAt)}</span>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-violet-400" />
        </button>
      ))}
    </div>
  )
}

export default function MergedTab({
  conversationId,
  mergedIntoId,
  onNavigateToConversation,
}: MergedTabProps): JSX.Element {
  const [primary, setPrimary] = useState<RelatedCard | null>(null)
  const [secondaries, setSecondaries] = useState<RelatedCard[]>([])
  const [loading, setLoading] = useState(false)

  // Primary of this secondary.
  useEffect(() => {
    if (!mergedIntoId) { setPrimary(null); return }
    let cancelled = false
    api.get<{ conversation: {
      id: string; referenceId: string; subject: string | null; status: string; openedAt: string | null
      displayName: string | null; primaryHandle: string | null; assignedAgentName: string | null; channelOrigin: string | null
    } }>(`/conversations/${mergedIntoId}`)
      .then((res) => {
        if (cancelled) return
        const p = res.conversation
        setPrimary({
          id: p.id, referenceId: p.referenceId, subject: p.subject, status: p.status,
          client: formatCustomerName(p.displayName, p.primaryHandle, p.channelOrigin),
          agent: p.assignedAgentName, priority: null,
          channel: p.channelOrigin, openedAt: p.openedAt,
        })
      })
      .catch(() => { if (!cancelled) setPrimary(null) })
    return () => { cancelled = true }
  }, [mergedIntoId])

  // Secondaries merged INTO this conversation.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get<{ items: Array<{
      id: string; referenceId: string; subject: string | null; status: string; openedAt: string | null
      displayName: string | null; primaryHandle: string | null; priorityLabel: string | null
      assignedAgentLoginId: string | null; channelOrigin: string | null
    }> }>(`/conversations?mergedIntoId=${conversationId}`)
      .then((res) => {
        if (cancelled) return
        setSecondaries((res.items || []).map((c) => ({
          id: c.id, referenceId: c.referenceId, subject: c.subject, status: c.status,
          client: formatCustomerName(c.displayName, c.primaryHandle, c.channelOrigin),
          agent: c.assignedAgentLoginId, priority: c.priorityLabel,
          channel: c.channelOrigin, openedAt: c.openedAt,
        })))
      })
      .catch(() => { if (!cancelled) setSecondaries([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [conversationId])

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-5">
      <div className="mb-5">
        <h3 className="text-lg font-semibold text-slate-800">Merged Conversations</h3>
        <p className="text-xs text-slate-400">Conversations combined with this one</p>
      </div>

      {primary && (
        <div className="mb-6">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            <GitMerge className="h-3.5 w-3.5 text-violet-500" /> Primary conversation
          </div>
          <p className="mb-2 text-[11px] text-slate-400">This conversation was merged into the primary below.</p>
          <RelatedCards cards={[primary]} onNavigate={onNavigateToConversation} />
        </div>
      )}

      {!mergedIntoId && (
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            <GitMerge className="h-3.5 w-3.5 text-violet-500" /> Merged secondary conversations{secondaries.length > 0 && ` (${secondaries.length})`}
          </div>
          {loading ? (
            <div className="py-4 text-center text-sm text-slate-400">Loading…</div>
          ) : secondaries.length === 0 ? (
            <div className={`flex flex-col items-center justify-center gap-2 ${R} border border-dashed border-slate-200 bg-white py-10 text-center`}>
              <GitMerge className="h-6 w-6 text-slate-300" />
              <p className="text-sm text-slate-400">No conversations have been merged into this one.</p>
            </div>
          ) : (
            <RelatedCards cards={secondaries} onNavigate={onNavigateToConversation} />
          )}
        </div>
      )}
    </div>
  )
}
